import React, { useState, useEffect, useCallback } from 'react';
import { dossiersApi } from '../../api/dossiers';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { useSourceViewer } from '../DocumentImport/SourceViewerContext';
import { citationShortId } from '../DocumentImport/importCitation';
import { SECRET_HALF_FEEDS_WRITE } from '../../utils/writerCopy';
import './CharacterDossier.css';

/**
 * CharacterDossier — character long write-up (spec §2 spine, §4 sign-off).
 * Lives on the Character note (`variant="page"`); `embedded` is leftover
 * modal chrome. No Dossiers room. Offer-shaped:
 * nothing auto-confirms; confirm/edit/refresh-accept are all writer decisions; gaps
 * read as "to develop", never as errors. Proposed/confirmed follow the Story Bible's
 * visual language (dashed+accent = proposed; solid+success = confirmed).
 */

// The fixed spine (backend services/character_dossier/fields.py), public then secret.
const PUBLIC_FIELDS = [
  ['identity_line', 'Identity'],
  ['physical_facts', 'Physical'],
  ['voice', 'Voice'],
  ['behavioral_constants', 'Behavioral constants'],
  ['want_and_history', 'Want & history'],
];
const SECRET_FIELDS = [
  ['wound_false_belief', 'Wound & false belief'],
  ['secrets_held', 'Secrets held'],
  ['knowledge_state', 'Knowledge at story start'],
];

function entryConflict(entry) {
  return (entry.provenance || []).some((p) => p && p.conflict);
}

export default function CharacterDossier({ entityId, entityName, variant = 'embedded' }) {
  const { openImportChunkCitation } = useSourceViewer();
  const [data, setData] = useState(undefined); // undefined=loading, null=none yet, obj=view
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false); // any confirm/edit/accept/reject in flight
  const [editing, setEditing] = useState(null); // { id, draft }
  const [expandedProv, setExpandedProv] = useState({}); // entryId -> bool
  const [refreshDiff, setRefreshDiff] = useState(null); // diff[] under review
  // True right after a generate() call whose own response came back with an
  // empty `proposed` list — distinguishes "thin story, nothing to build from"
  // (a real, informative outcome) from "never generated yet" so the empty
  // state doesn't just silently re-offer the same button with no explanation.
  // Reset whenever a fresh load() actually finds entries, or the entity
  // changes.
  const [generatedEmpty, setGeneratedEmpty] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await dossiersApi.get(entityId);
      setData(res || null);
      if ((res?.entries || []).length > 0) setGeneratedEmpty(false);
    } catch (e) {
      setError(e.message || 'Failed to load dossier');
      setData(null);
    }
  }, [entityId]);

  useEffect(() => { setData(undefined); setGeneratedEmpty(false); load(); }, [load]);

  const handleGenerate = async () => {
    const ok = await askPaid('character_note');
    if (!ok) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await dossiersApi.generate(entityId);
      setGeneratedEmpty((res?.proposed || []).length === 0);
      await load();
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmEntry = async (entry) => {
    setBusy(true);
    try {
      await dossiersApi.confirm(entityId, { entryIds: [entry.id] });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleConfirmHalf = async (half) => {
    setBusy(true);
    try {
      await dossiersApi.confirm(entityId, { half });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await dossiersApi.edit(editing.id, editing.draft);
      setEditing(null);
      await load(); // reload so a superseding entry (not a mutation) shows through
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleRefresh = async (force = false) => {
    const ok = await askPaid('character_note', {
      title: force ? 'Regenerate' : 'Refresh from sources',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await dossiersApi.refresh(entityId, { force: !!force });
      setRefreshDiff(res.diff || []);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleAccept = async (entryId) => {
    setBusy(true);
    try {
      await dossiersApi.accept(entryId);
      setRefreshDiff((d) => d.filter((it) => it.proposed.id !== entryId));
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleReject = async (entryId) => {
    setBusy(true);
    try {
      await dossiersApi.reject(entryId);
      setRefreshDiff((d) => d.filter((it) => it.proposed.id !== entryId));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // ── render helpers ──────────────────────────────────────────────────────────

  const renderEntry = (entry) => {
    const proposed = entry.status === 'proposed';
    const conflict = entryConflict(entry);
    const isEditing = editing && editing.id === entry.id;
    const provCount = (entry.provenance || []).length;
    return (
      <div
        key={entry.id}
        className={`cd-entry ${proposed ? 'cd-entry--proposed' : 'cd-entry--confirmed'}`}
      >
        <div className="cd-entry__head">
          <span className={`cd-entry__badge cd-entry__badge--${proposed ? 'proposed' : 'confirmed'}`}>
            {proposed ? 'Proposed' : 'Confirmed'}
          </span>
          {conflict && <span className="cd-entry__badge cd-entry__badge--conflict">Conflict</span>}
        </div>

        {isEditing ? (
          <div className="cd-entry__edit">
            <textarea
              className="cd-entry__textarea"
              value={editing.draft}
              onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
              rows={3}
              autoFocus
            />
            <div className="cd-entry__actions">
              <button className="cd-btn cd-btn--primary" disabled={busy} onClick={handleSaveEdit}>Save</button>
              <button className="cd-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {entry.not_established ? (
              <div className="cd-entry__gap">— to develop —</div>
            ) : (
              <div className="cd-entry__content">{entry.content}</div>
            )}

            {conflict && (
              <div className="cd-entry__conflict-note">
                Sources conflict here — resolve by editing, then confirm.
              </div>
            )}

            {provCount > 0 && (
              <button
                type="button"
                className="cd-entry__prov"
                onClick={() => setExpandedProv((m) => ({ ...m, [entry.id]: !m[entry.id] }))}
              >
                {provCount} source{provCount === 1 ? '' : 's'}
                {expandedProv[entry.id] ? ' ▾' : ' ▸'}
              </button>
            )}
            {expandedProv[entry.id] && (
              <div className="cd-entry__prov-list">
                {(entry.provenance || []).map((p, i) => (
                  p?.type === 'import_chunk' && p.id ? (
                    <button
                      key={i}
                      type="button"
                      className="cd-prov-tag cd-prov-tag--import"
                      onClick={() => openImportChunkCitation(
                        citationShortId(p.id),
                        { id: p.id, kind: 'import_chunk' },
                      )}
                    >
                      import_chunk{p.conflict ? ' · conflict' : ''}
                    </button>
                  ) : (
                    <span key={i} className="cd-prov-tag">{p.type}{p.conflict ? ' · conflict' : ''}</span>
                  )
                ))}
              </div>
            )}

            <div className="cd-entry__actions">
              {proposed && (
                <button className="cd-btn cd-btn--primary" disabled={busy}
                  onClick={() => handleConfirmEntry(entry)}>Confirm</button>
              )}
              <button className="cd-btn" disabled={busy}
                onClick={() => setEditing({ id: entry.id, draft: entry.not_established ? '' : (entry.content || '') })}>
                Edit
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderHalf = (label, fields, half) => {
    const entries = data?.entries || [];
    const byField = {};
    entries.filter((e) => e.half === half).forEach((e) => {
      (byField[e.field] = byField[e.field] || []).push(e);
    });
    const present = fields.filter(([key]) => byField[key]?.length);
    const hasProposed = entries.some((e) => e.half === half && e.status === 'proposed');
    return (
      <section className="cd-half">
        <div className="cd-half__head">
          <h4 className="cd-half__title">{label}</h4>
          {hasProposed && (
            <button className="cd-btn cd-btn--ghost" disabled={busy}
              onClick={() => handleConfirmHalf(half)}>
              Confirm all {half}
            </button>
          )}
        </div>
        {half === 'secret' && (
          <p className="cd-half__hint">{SECRET_HALF_FEEDS_WRITE}</p>
        )}
        {present.length === 0 ? (
          <div className="cd-half__empty">Nothing here yet.</div>
        ) : (
          present.map(([key, fieldLabel]) => (
            <div key={key} className="cd-field">
              <div className="cd-field__label">{fieldLabel}</div>
              {byField[key].map(renderEntry)}
            </div>
          ))
        )}
      </section>
    );
  };

  const renderRelationships = () => {
    const rels = data?.relationships || [];
    if (rels.length === 0) return null;
    return (
      <section className="cd-rels">
        <div className="cd-rels__head">
          <h4 className="cd-half__title">Relationships</h4>
          <span className="cd-rels__note">live from the graph · read-only</span>
        </div>
        <div className="cd-rels__list">
          {rels.map((r, i) => {
            const pred = r.predicate || r.predicate_display;
            const other = r.entity_b_name || r.entity_a_name || '—';
            return (
              <span key={r.id || i} className="cd-rel">
                {pred ? `${pred} ` : ''}<strong>{other}</strong>
              </span>
            );
          })}
        </div>
      </section>
    );
  };

  const renderDiffReview = () => (
    <section className="cd-diff">
      <div className="cd-diff__head">
        <h4 className="cd-half__title">Refresh — proposed changes</h4>
        <span className="cd-rels__note">accept or reject each; nothing changes until you do</span>
      </div>
      {refreshDiff.length === 0 ? (
        <div className="cd-half__empty">No changes proposed — the dossier already matches the sources.</div>
      ) : (
        refreshDiff.map((it) => (
          <div key={it.proposed.id} className="cd-entry cd-entry--proposed">
            <div className="cd-entry__head">
              <span className="cd-entry__badge cd-entry__badge--proposed">
                {it.change === 'supersede' ? 'Would replace' : 'Would add'}
              </span>
              <span className="cd-diff__field">{it.proposed.field}</span>
            </div>
            <div className="cd-entry__content">
              {it.proposed.not_established ? <em>— to develop —</em> : it.proposed.content}
            </div>
            {it.supersedes && <div className="cd-entry__conflict-note">Replaces an existing confirmed entry.</div>}
            <div className="cd-entry__actions">
              <button className="cd-btn cd-btn--primary" disabled={busy}
                onClick={() => handleAccept(it.proposed.id)}>Accept</button>
              <button className="cd-btn" disabled={busy}
                onClick={() => handleReject(it.proposed.id)}>Reject</button>
            </div>
          </div>
        ))
      )}
      <div className="cd-entry__actions">
        <button className="cd-btn" disabled={busy} onClick={() => { setRefreshDiff(null); }}>Done reviewing</button>
      </div>
    </section>
  );

  // ── top-level render ──────────────────────────────────────────────────────

  const rootClass = variant === 'page' ? 'cd-root cd-root--page' : 'cd-root';

  if (data === undefined) {
    return <div className={`${rootClass} cd-state`}>Loading dossier…</div>;
  }

  const isEmpty = data === null || (data.entries || []).length === 0;

  return (
    <div className={rootClass}>
      {paidDialog}
      <div className="cd-toolbar">
        <span className="cd-toolbar__title">Character dossier</span>
        {!isEmpty && !refreshDiff && (
          <div className="cd-toolbar__actions">
            <button className="cd-btn cd-btn--ghost" disabled={busy || generating} onClick={() => handleRefresh(false)}>
              Refresh from sources
            </button>
            <button className="cd-btn cd-btn--ghost" disabled={busy || generating} onClick={() => handleRefresh(true)}>
              Regenerate
            </button>
          </div>
        )}
      </div>

      {error && <div className="cd-error">{error}</div>}

      {refreshDiff ? (
        renderDiffReview()
      ) : isEmpty ? (
        <div className="cd-empty">
          {generatedEmpty ? (
            <>
              <p className="cd-empty__lead">Not enough session material yet to build this dossier.</p>
              <p className="cd-empty__hint">
                {entityName ? `Brainstorm sessions that mention ${entityName}` : 'Brainstorm sessions that mention this character'} —
                who they are, how they act, what they want — give the dossier something to draw
                from. Nothing was invented in place of that; try again once a session or two has
                touched on them.
              </p>
            </>
          ) : (
            <>
              <p className="cd-empty__lead">No note yet. What you need is still in the chat.</p>
              <p className="cd-empty__hint">
                Make a note from your sessions when you want one. Every entry
                arrives as a proposal you confirm, edit, or leave — nothing is committed for you.
              </p>
            </>
          )}
          <button className="cd-btn cd-btn--primary" disabled={generating} onClick={handleGenerate}>
            {generating ? 'Generating… (this takes a moment)' : generatedEmpty ? 'Try again' : 'Make a note'}
          </button>
        </div>
      ) : (
        <>
          {generating && <div className="cd-state">Generating…</div>}
          {renderHalf('Public', PUBLIC_FIELDS, 'public')}
          {renderHalf('Secret', SECRET_FIELDS, 'secret')}
          {renderRelationships()}
        </>
      )}
    </div>
  );
}
